/* eslint-disable jsx-a11y/accessible-emoji */

import { Box, Button, Grid, Text, useToast } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdEmail, MdFileDownload, MdPictureAsPdf } from "react-icons/md";
import { useParams } from "react-router-dom";
import { Comment } from "../../components/Comment";
import { Loader } from "../../components/Loader";
import { Title } from "../../components/Title";
import {
  CommentForm,
  ICreateMedicalFormValues,
  MedicalCardForm,
} from "../../forms";
import {
  GetMedicalCardDocument,
  useCreateMedicalCardCommentMutation,
  useDeleteMedicalCardCommentMutation,
  useGenerateMedicalCardPdfMutation,
  useGetMedicalCardQuery,
  useGetRecipeEmailTextForMedicalCardMutation,
  useMeQuery,
  useUpdateMedicalCardCommentMutation,
  useUpdateMedicalCardMutation,
} from "../../graphql";
import { useDashboardTitle } from "../../hooks/useDashboardTitle";
import { download, formatDateTime, getFullName } from "../../utils";

export const MedicalCardInfo: React.FC = () => {
  const { t } = useTranslation();
  const { setTitle } = useDashboardTitle();
  const { visitId } = useParams<{ visitId: string }>();
  const toast = useToast();

  const { data: observer, loading: loadingMe } = useMeQuery({
    fetchPolicy: "cache-first",
  });
  const [updateMedicalCard] = useUpdateMedicalCardMutation();
  const [createMedicalCardComment] = useCreateMedicalCardCommentMutation();
  const { data, loading } = useGetMedicalCardQuery({
    variables: {
      id: parseInt(visitId),
    },
  });
  const [updateMedicalCardComment] = useUpdateMedicalCardCommentMutation();
  const [deleteMedicalCardComment] = useDeleteMedicalCardCommentMutation();
  const [getRecipeEmail] = useGetRecipeEmailTextForMedicalCardMutation();
  const [generateMedicalCardPdf, { loading: loadingPDF }] =
    useGenerateMedicalCardPdfMutation();

  const medicalCard = data?.getMedicalCard;

  const onMedicalFormSumbit = async (values: ICreateMedicalFormValues) => {
    await updateMedicalCard({
      variables: {
        id: medicalCard.id,
        symptoms: values.symptoms,
        diagnosis: values.diagnosis,
        recommendation: values.recommendation,
        recipe: values.recipe,
        privateNote: values.privateNote,
        medicalConcernCategoryId: parseInt(values.medicalConcernCategoryId),
      },
    });

    toast({
      title: t("forms.success"),
      description: t("forms.changesSaved"),
      status: "success",
      isClosable: true,
    });
  };

  const onCommentSumbit = async (values) => {
    await createMedicalCardComment({
      variables: {
        medicalCardId: medicalCard.id,
        content: values.content,
      },
      refetchQueries: [
        {
          query: GetMedicalCardDocument,
          variables: {
            id: parseInt(visitId),
          },
        },
      ],
    });

    toast({
      title: t("forms.success"),
      description: t("patients.medicalCards.detailsTab.commentAdded"),
      status: "success",
      isClosable: true,
    });
  };

  const onCommentUpdate = async (id, content) => {
    await updateMedicalCardComment({
      variables: {
        id: id,
        content: content,
      },
      refetchQueries: [
        {
          query: GetMedicalCardDocument,
          variables: {
            id: parseInt(visitId),
          },
        },
      ],
    });

    toast({
      title: t("forms.success"),
      description: t("patients.medicalCards.detailsTab.commentEdited"),
      status: "success",
      isClosable: true,
    });
  };

  const onCommentDelete = async (id) => {
    await deleteMedicalCardComment({
      variables: {
        id,
      },
      refetchQueries: [
        {
          query: GetMedicalCardDocument,
          variables: {
            id: parseInt(visitId),
          },
        },
      ],
    });

    toast({
      title: t("forms.success"),
      description: t("patients.medicalCards.detailsTab.commentDeleted"),
      status: "success",
      isClosable: true,
    });
  };

  const onSendEmail = async () => {
    const { data: recipeData } = await getRecipeEmail({
      variables: {
        id: +visitId,
      },
      fetchPolicy: "no-cache",
    });

    window.open(
      `mailto:?cc=&subject=Recetë&body=${recipeData?.getRecipeEmailTextForMedicalCard.replace(
        /\n/g,
        "%0D%0A"
      )}`
    );
  };

  const onPDFGenerate = async () => {
    const { data: pdfData } = await generateMedicalCardPdf({
      variables: {
        medicalCardId: +visitId,
      },
      fetchPolicy: "no-cache",
    });

    download(pdfData.generateMedicalCardPDF.url);
  };

  useEffect(() => {
    if (!loading) {
      setTitle([
        t("patients.medicalCards.detailsTab.accounts"),
        {
          to: `/account/${medicalCard?.patient?.id}`,
          text: getFullName(medicalCard?.patient),
        },
        t("patients.medicalCards.detailsTab.visitOnDate", {
          date: formatDateTime(medicalCard.createdAt),
        }),
      ]);
    }

    // eslint-disable-next-line
  }, [loading]);

  return (
    <>
      {loading || loadingMe ? (
        <Loader />
      ) : (
        <>
          <Grid templateColumns="repeat(2, 1fr)" gap={6}>
            <Box
              borderWidth="1px"
              borderRadius="lg"
              overflow="hidden"
              background="white"
            >
              <Title title={t("patients.medicalCards.detailsTab.details")}>
                <Button
                  rightIcon={<MdFileDownload />}
                  colorScheme="blue"
                  size="sm"
                  variant="ghost"
                  onClick={onPDFGenerate}
                  marginRight="10px"
                  isLoading={loadingPDF}
                >
                  {t("patients.medicalCards.detailsTab.download")}
                </Button>
                <Button
                  rightIcon={<MdEmail />}
                  colorScheme="blue"
                  size="sm"
                  variant="ghost"
                  onClick={onSendEmail}
                  marginRight="10px"
                >
                  {t("patients.medicalCards.detailsTab.recipe")}
                </Button>
              </Title>
              <MedicalCardForm
                onSubmit={onMedicalFormSumbit}
                initialValues={{
                  diagnosis: medicalCard.diagnosis,
                  recipe: medicalCard.recipe,
                  recommendation: medicalCard.recommendation,
                  symptoms: medicalCard.symptoms,
                  privateNote: medicalCard.privateNote,
                  medicalConcernCategoryId: medicalCard.concernCategory?.id
                    ? String(medicalCard.concernCategory.id)
                    : "",
                }}
              />
            </Box>

            <Box
              borderWidth="1px"
              borderRadius="sm"
              overflow="hidden"
              background="white"
            >
              <Title
                title={t("patients.medicalCards.detailsTab.comments")}
              ></Title>
              {medicalCard.comments.map((comment) => (
                <Comment
                  key={comment.id}
                  comment={comment}
                  loggedUser={observer}
                  onSubmit={onCommentUpdate}
                  onDelete={onCommentDelete}
                />
              ))}

              <CommentForm
                onSubmit={onCommentSumbit}
                initialValues={{ content: "" }}
              ></CommentForm>
            </Box>
          </Grid>
        </>
      )}
    </>
  );
};
